import { Link } from 'react-router-dom';
import { Users, Eye, MousePointer, Keyboard, Volume2, CheckCircle } from 'lucide-react';

function AccessibilityStatement() {
  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex items-center justify-center mb-6">
            <Users className="w-20 h-20 text-gold" />
          </div>
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Digital Inclusion</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Accessibility <span className="text-gold">Statement</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-4xl mx-auto">
            Williams Diversified LLC is committed to ensuring digital accessibility for people with disabilities.
          </p>
          <p className="text-gray-400 text-sm mt-4">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      {/* Commitment Overview Cards */}
      <section className="bg-black py-16 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Eye className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Visual Access</h3>
              <p className="text-gray-400">Screen reader compatible with high contrast design</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Keyboard className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Keyboard Navigation</h3>
              <p className="text-gray-400">Full keyboard accessibility throughout the site</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <CheckCircle className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">WCAG Compliance</h3>
              <p className="text-gray-400">Striving for WCAG 2.1 Level AA standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-black py-16 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gray-300 space-y-12">
            
            {/* Section 1: Our Commitment */}
            <div className="pt-28">
              <div className="flex items-center space-x-3 mb-6">
                <Users className="w-8 h-8 text-gold" />
                <h2 className="text-3xl font-bold text-white font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  1. Our Commitment to Accessibility
                </h2>
              </div>
              <div className="space-y-4 text-lg">
                <p>
                  Williams Diversified LLC is committed to ensuring that our website is accessible to all users, including people with disabilities. We believe that everyone should have equal access to information about our services, regardless of their abilities or the technologies they use.
                </p>
                <p>
                  We are continually working to improve the accessibility and usability of our website to ensure we provide equal access to all of our users. Our goal is to meet or exceed the requirements of the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA.
                </p>
              </div>
            </div>

            {/* Section 2: Accessibility Standards */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                2. Accessibility Standards
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Our website aims to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. These guidelines explain how to make web content more accessible for people with disabilities and user-friendly for everyone.
                </p>
                <p>
                  The guidelines have three levels of accessibility (A, AA, and AAA). We have chosen Level AA as our target because it represents a reasonable standard that balances user needs with technical capabilities.
                </p>
              </div>
            </div>

            {/* Section 3: Accessibility Features */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                3. Accessibility Features
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Our website includes the following accessibility features:
                </p>
                
                <div className="space-y-6 mt-6">
                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <div className="flex items-start space-x-4">
                      <Eye className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
                      <div className="pt-28">
                        <h3 className="text-xl font-bold text-white mb-3">Visual Accessibility</h3>
                        <ul className="space-y-2 text-gray-300">
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>High contrast color scheme (black background with gold accents)</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Clear, readable fonts with appropriate sizing</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Text alternatives for images and visual content</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Consistent navigation and page structure</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <div className="flex items-start space-x-4">
                      <Keyboard className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
                      <div className="pt-28">
                        <h3 className="text-xl font-bold text-white mb-3">Keyboard Navigation</h3>
                        <ul className="space-y-2 text-gray-300">
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Full keyboard accessibility without requiring a mouse</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Visible focus indicators for interactive elements</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Logical tab order throughout the website</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Skip navigation links to bypass repetitive content</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <div className="flex items-start space-x-4">
                      <MousePointer className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
                      <div className="pt-28">
                        <h3 className="text-xl font-bold text-white mb-3">Screen Reader Compatibility</h3>
                        <ul className="space-y-2 text-gray-300">
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Semantic HTML markup for proper content structure</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>ARIA labels and landmarks for navigation</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Descriptive link text and button labels</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Alternative text for all meaningful images</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <div className="flex items-start space-x-4">
                      <Volume2 className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
                      <div className="pt-28">
                        <h3 className="text-xl font-bold text-white mb-3">Content Accessibility</h3>
                        <ul className="space-y-2 text-gray-300">
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Clear, concise language throughout the site</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Proper heading hierarchy for content organization</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Responsive design for various devices and screen sizes</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gold mr-3">•</span>
                            <span>Forms with clear labels and error messages</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Compatible Technologies */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                4. Compatible Technologies
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Our website is designed to be compatible with the following assistive technologies:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Screen Readers:</strong> JAWS, NVDA, VoiceOver, TalkBack</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Browsers:</strong> Latest versions of Chrome, Firefox, Safari, Edge</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Operating Systems:</strong> Windows, macOS, iOS, Android</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Keyboard Navigation:</strong> All major operating systems and browsers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Magnification Software:</strong> ZoomText and browser zoom functionality</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 5: Known Limitations */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                5. Known Limitations
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Despite our best efforts to ensure accessibility, there may be some limitations. Below is a description of known limitations and potential solutions:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Third-Party Content:</strong> Some third-party embedded content may not be fully accessible. We are working with our vendors to improve this.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">PDF Documents:</strong> Some downloadable documents may not be fully accessible. Please contact us if you need an accessible version.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Legacy Content:</strong> Older content may not meet current accessibility standards. We are in the process of updating this content.</span>
                  </li>
                </ul>
                <p className="mt-4">
                  We are actively working to resolve these limitations and welcome feedback to help us improve.
                </p>
              </div>
            </div>

            {/* Section 6: Ongoing Efforts */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                6. Ongoing Accessibility Efforts
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We are committed to continuous improvement of our website's accessibility. Our ongoing efforts include:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Regular accessibility audits and testing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Staff training on accessibility best practices</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Incorporating accessibility from the earliest stages of design and development</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Monitoring and responding to user feedback</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Updating content and features to meet evolving accessibility standards</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 7: Feedback and Contact */}
            <div className="bg-black border border-gold/30 rounded-lg p-8 mt-12">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                7. Feedback and Contact Information
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We welcome your feedback on the accessibility of our website. If you encounter any accessibility barriers or have suggestions for improvement, please let us know:
                </p>
                <div className="space-y-3 mt-6">
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Company:</div>
                    <div className="pt-28">Williams Diversified LLC</div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Email:</div>
                    <div className="pt-28">
                      <a href="mailto:inquire@williamsdiversified.com" className="hover:text-gold transition-colors">
                        inquire@williamsdiversified.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Phone:</div>
                    <div className="pt-28">
                      <a href="tel:9184933418" className="hover:text-gold transition-colors">
                        (918) 493-3418
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Location:</div>
                    <div className="pt-28">Tulsa, Oklahoma</div>
                  </div>
                </div>
                <p className="mt-6">
                  We aim to respond to accessibility feedback within 5 business days and to propose a solution within 10 business days. If you need immediate assistance accessing information on our website, please call us directly.
                </p>
              </div>
            </div>

            {/* Section 8: Alternative Access */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                8. Alternative Access Methods
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  If you are unable to access any content or features on our website, we offer alternative methods to obtain information and services:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Phone Support:</strong> Call us at (918) 493-3418 for direct assistance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Email Communication:</strong> Email us at inquire@williamsdiversified.com for written correspondence</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Alternative Formats:</strong> We can provide documents in alternative formats (large print, accessible PDF, etc.) upon request</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">In-Person Assistance:</strong> We welcome in-person meetings to discuss your project needs</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 9: Technical Specifications */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                9. Technical Specifications
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Accessibility of this website relies on the following technologies to work:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>HTML5 for semantic structure</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>CSS for visual presentation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>JavaScript for interactive functionality</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>WAI-ARIA for enhanced accessibility</span>
                  </li>
                </ul>
                <p className="mt-4">
                  These technologies are relied upon for conformance with the accessibility standards used.
                </p>
              </div>
            </div>

            {/* Section 10: Formal Complaints */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                10. Formal Complaints
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  If you are not satisfied with our response to your accessibility concerns, you may file a formal complaint. We take all complaints seriously and will investigate them thoroughly.
                </p>
                <p>
                  In addition to contacting us directly, you may also file a complaint with relevant authorities if you believe we have not adequately addressed accessibility issues.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Need <span className="text-gold">Assistance?</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            We're here to help you access our services and information
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default AccessibilityStatement;
